/**
 * Pure, read-only diff engine for the save-conflict Phase 2 view — compares
 * two independently-loaded copies of the same guide's places/events and
 * classifies each as added/removed/changed. No React/redux/DataBase
 * coupling, so it's reusable by a later interactive-merge increment too.
 *
 * Place IDs are stable across save/reload (Place.js preserves `ID` verbatim
 * on decode), so places are matched by ID.
 *
 * Event IDs are NOT stable — Schedule.js's decodeSchedule mints a fresh
 * random ID on every decode, even for an event read from existing JSON — so
 * events are matched by a content key (Subject+Places) instead. Start/End
 * are deliberately NOT part of the match key: they need to be comparable as
 * a "changed" field (a pure time edit should show old time -> new time, not
 * look like one event removed and a different one added).
 */

const PLACE_FIELDS = [
  "Name", "Lat", "Longi", "Address", "Country", "State", "City", "Phone",
  "Info", "Open", "Hlink", "Image", "Email", "MustSee", "Favorite",
  "Visited", "Duration",
];

// Lat/Longi get exact-compared like every other field EXCEPT for a small
// tolerance — found 2026-10-02 via a real conflict surfacing a Lat/Longi
// "change" of 45.4640976 vs 45.464098, i.e. the 7th decimal digit, ~5cm of
// real-world distance. Nobody edits a place's position by 5cm; this is
// rounding noise from something in the pipeline (e.g. Google Maps snaps a
// dragged marker's read-back position to ~6 decimal places), and forcing a
// field-by-field choice on noise like that is pure friction. 0.00001° is
// ~1m at these latitudes — comfortably below "a human moved the pin" and
// comfortably above float/rounding drift.
const COORD_EPSILON = 0.00001;
const coordsEqual = (a, b) => {
  const fa = parseFloat(a);
  const fb = parseFloat(b);
  if (Number.isNaN(fa) || Number.isNaN(fb)) return String(a ?? "") === String(b ?? "");
  return Math.abs(fa - fb) < COORD_EPSILON;
};

const arraysEqualUnordered = (a, b) => {
  const sa = [...(a || [])].sort();
  const sb = [...(b || [])].sort();
  return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
};

const diffPlaceFields = (localPlace, serverPlace) => {
  const changedFields = [];
  PLACE_FIELDS.forEach((field) => {
    if (field === "Lat" || field === "Longi") {
      if (!coordsEqual(localPlace[field], serverPlace[field])) changedFields.push(field);
      return;
    }
    if (JSON.stringify(localPlace[field]) !== JSON.stringify(serverPlace[field])) changedFields.push(field);
  });
  if (!arraysEqualUnordered(localPlace.Category, serverPlace.Category)) changedFields.push("Category");
  if (!arraysEqualUnordered(localPlace.Group, serverPlace.Group)) changedFields.push("Group");
  return changedFields;
};

// localPlacesList/serverPlacesList: arrays of Place (e.g. guide.places.placesList).
export function diffPlaces(localPlacesList, serverPlacesList) {
  const added = [];
  const removed = [];
  const changed = [];
  const localById = new Map((localPlacesList || []).map((p) => [p.ID, p]));
  const serverById = new Map((serverPlacesList || []).map((p) => [p.ID, p]));

  serverById.forEach((serverPlace, id) => {
    if (!localById.has(id)) added.push(serverPlace);
  });
  localById.forEach((localPlace, id) => {
    if (!serverById.has(id)) removed.push(localPlace);
  });
  localById.forEach((localPlace, id) => {
    const serverPlace = serverById.get(id);
    if (!serverPlace) return;
    const changedFields = diffPlaceFields(localPlace, serverPlace);
    if (changedFields.length > 0) changed.push({ local: localPlace, server: serverPlace, changedFields });
  });

  return { added, removed, changed };
}

// Places is a CSV string with no guaranteed order, and nothing canonicalizes
// it before it's written back (Schedule.js) — so the identical set of
// places, written back in a different order, hashes to a different key here.
// That turned one real edit into a false "removed" (old order) + "added"
// (new order) pair, i.e. the same event visually duplicated in the diff.
// Sorting before joining makes the key order-independent. Found 2026-10-03.
const eventKey = (event) => {
  const places = (event.Places || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .sort()
    .join(",");
  return `${event.Subject}||${places}`;
};

// Exported for anything outside this diff engine that needs to reference
// "this event" past the current in-memory session (e.g. comments'
// target_id) — event.ID itself is regenerated randomly on every decode (see
// this file's own header comment), so it can't be used for that. Qualified
// by Itinerary too, unlike the diff's own internal eventKey above: diffEvents
// groups by Itinerary externally before matching within a group, so two
// identical Subject+Places events in different itineraries (e.g.
// "Breakfast" on Day 1 and Day 2) would otherwise collide here.
export function stableEventKey(event) {
  return `${event.Itinerary || "Main"}||${eventKey(event)}`;
}

const diffEventFields = (localEvent, serverEvent) => {
  const changedFields = [];
  ["Start", "End", "Body"].forEach((field) => {
    if (String(localEvent[field]) !== String(serverEvent[field])) changedFields.push(field);
  });
  return changedFields;
};

const diffEventList = (localList, serverList) => {
  const added = [];
  const removed = [];
  const changed = [];

  // Grouped by key (not a 1:1 Map) — two events can legitimately share the
  // same Subject+Places within one itinerary (e.g. a "Breakfast" placeholder
  // repeated once per day, same comment above already calls this out across
  // itineraries). A plain Map silently collapses same-key duplicates to
  // just the last one, which can misattribute an edit on one of them to an
  // unrelated sibling, or drop it from the comparison entirely. Pairing by
  // index within each key's group instead keeps same-day instances matched
  // to each other, as long as both lists keep their original (chronological)
  // order. Found 2026-10-03.
  const groupByKey = (list) => {
    const map = new Map();
    list.forEach((e) => {
      const key = eventKey(e);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  };
  const localGroups = groupByKey(localList);
  const serverGroups = groupByKey(serverList);
  const allKeys = new Set([...localGroups.keys(), ...serverGroups.keys()]);

  allKeys.forEach((key) => {
    const localArr = localGroups.get(key) || [];
    const serverArr = serverGroups.get(key) || [];
    const pairCount = Math.min(localArr.length, serverArr.length);
    for (let i = 0; i < pairCount; i++) {
      const changedFields = diffEventFields(localArr[i], serverArr[i]);
      if (changedFields.length > 0) changed.push({ local: localArr[i], server: serverArr[i], changedFields });
    }
    for (let i = pairCount; i < serverArr.length; i++) added.push(serverArr[i]);
    for (let i = pairCount; i < localArr.length; i++) removed.push(localArr[i]);
  });

  return { added, removed, changed };
};

// localEvents/serverEvents: flat arrays of Appointment (e.g. guide.schedule.events).
// Returns { [itineraryName]: { added, removed, changed } } — events are
// grouped by their Itinerary tag first, since a guide's "schedules" are
// really one flat array segmented by that field, and an event moved between
// itineraries should show as removed-from-one/added-to-the-other rather
// than being matched across groups.
export function diffEvents(localEvents, serverEvents) {
  const groupByItinerary = (events) => {
    const map = new Map();
    (events || []).forEach((e) => {
      const key = e.Itinerary || "Main";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  };
  const localByItin = groupByItinerary(localEvents);
  const serverByItin = groupByItinerary(serverEvents);
  const itineraryNames = new Set([...localByItin.keys(), ...serverByItin.keys()]);

  const result = {};
  itineraryNames.forEach((itin) => {
    result[itin] = diffEventList(localByItin.get(itin) || [], serverByItin.get(itin) || []);
  });
  return result;
}

// Convenience summary for the collapsed dialog line, e.g.
// "3 places added, 1 changed · 2 events changed".
export function summarizeDiff(placesDiff, eventsDiffByItinerary) {
  const eventTotals = Object.values(eventsDiffByItinerary || {}).reduce(
    (acc, d) => ({
      added: acc.added + d.added.length,
      removed: acc.removed + d.removed.length,
      changed: acc.changed + d.changed.length,
    }),
    { added: 0, removed: 0, changed: 0 }
  );
  const parts = [];
  const placeBits = [];
  if (placesDiff.added.length) placeBits.push(`${placesDiff.added.length} added`);
  if (placesDiff.removed.length) placeBits.push(`${placesDiff.removed.length} removed`);
  if (placesDiff.changed.length) placeBits.push(`${placesDiff.changed.length} changed`);
  if (placeBits.length) parts.push(`${placeBits.join(", ")} place${placesDiff.added.length + placesDiff.removed.length + placesDiff.changed.length === 1 ? "" : "s"}`);

  const eventBits = [];
  if (eventTotals.added) eventBits.push(`${eventTotals.added} added`);
  if (eventTotals.removed) eventBits.push(`${eventTotals.removed} removed`);
  if (eventTotals.changed) eventBits.push(`${eventTotals.changed} changed`);
  if (eventBits.length) parts.push(`${eventBits.join(", ")} event${eventTotals.added + eventTotals.removed + eventTotals.changed === 1 ? "" : "s"}`);

  return parts.length > 0 ? parts.join(" · ") : "No differences found in places or events.";
}

// Itinerary is a single freeform HTML string (Guide.getDocumentHTML()) — not
// a collection, so unlike places/events there's no per-field breakdown to
// offer. Whole-document compare only, by explicit design: HTML can't be
// finely diffed/merged without risking mangling the author's formatting.
export function diffItinerary(localDocumentHtml, serverDocumentHtml) {
  const local = localDocumentHtml || "";
  const server = serverDocumentHtml || "";
  return { changed: local !== server, local, server };
}

// Guide-level metadata (GuideAttributes, backed by the guide_info Firestore
// doc) — a single object like the itinerary, but unlike the itinerary it
// naturally breaks into discrete, independently-editable fields, so it gets
// the SAME per-field mine/theirs treatment as a changed place/event rather
// than one whole-object choice. Deliberately excludes fields that are
// internal bookkeeping, not user edits (mod, created, read, copy, rating,
// num_ratings, times_read, times_copied, owner_name, user_id, creator_id,
// guide_id, type, base_location) — those shouldn't ever be "picked" by a
// user resolving a content conflict.
export const GUIDE_INFO_FIELDS = ["guide_name", "description", "image", "image_m", "language", "countries", "cities", "access"];
export const GUIDE_INFO_LABELS = {
  guide_name: "Name",
  description: "Description",
  image: "Cover Photo",
  image_m: "Mobile Cover Photo",
  language: "Language",
  countries: "Countries",
  cities: "Cities",
  access: "Sharing",
};

// localAttributes: window.guide_obj.attributes (GuideAttributes instance).
// serverGuideInfo: the guide_info doc already fetched for the conflict dialog
// (infoDoc.data(), passed down as a prop) — no extra fetch needed, unlike
// places/events/itinerary which live in the separate guide_data doc.
export function diffGuideInfo(localAttributes, serverGuideInfo) {
  const changedFields = [];
  GUIDE_INFO_FIELDS.forEach((field) => {
    const localVal = localAttributes ? localAttributes[field] : undefined;
    const serverVal = serverGuideInfo ? serverGuideInfo[field] : undefined;
    if (Array.isArray(localVal) || Array.isArray(serverVal)) {
      if (!arraysEqualUnordered(localVal, serverVal)) changedFields.push(field);
    } else if (JSON.stringify(localVal ?? null) !== JSON.stringify(serverVal ?? null)) {
      changedFields.push(field);
    }
  });
  return { changed: changedFields.length > 0, changedFields, local: localAttributes, server: serverGuideInfo };
}

// Guide Settings (Guide.guide_settings) is a deeply-nested display-
// preferences object (map zoom, hide_schedule, wrap_text, show_photo_icons,
// places_list/places_map/places_map_hover sub-objects with many booleans) —
// cosmetic view prefs, not content a user thinks about field-by-field.
// Whole-object compare only, same reasoning/pattern as diffItinerary.
export function diffGuideSettings(localGuideSettings, serverGuideSettings) {
  const local = localGuideSettings || {};
  const server = serverGuideSettings || {};
  return { changed: JSON.stringify(local) !== JSON.stringify(server), local, server };
}

// --- Merge application (pure; no React/redux/DataBase coupling, matching
// this file's existing design) ---
//
// selections shape for places: { [placeID]: { included: bool } | { field: "mine"|"theirs", ... } }
// Simpler: callers pass two maps -
//   includeSelections: { [id]: bool } for added/removed items (key = the item's identity key)
//   fieldSelections: { [id]: { [fieldName]: "mine"|"theirs" } } for changed items
//
// Base is the SERVER's current list (the actual source of truth in
// Firestore) with local's included adds/removed-but-kept-back layered on top,
// then changed items get the per-field winner applied field-by-field.

function applyFieldChoices(localItem, serverItem, changedFields, fieldChoices) {
  // Object.create+assign (not a plain {...spread}) so a merged Place/
  // Appointment stays a real class instance — these carry methods the rest
  // of the app relies on (e.g. map rendering, instanceof checks), which a
  // flattened plain object would silently lose.
  const merged = Object.assign(Object.create(Object.getPrototypeOf(serverItem)), serverItem);
  changedFields.forEach((field) => {
    const choice = fieldChoices && fieldChoices[field];
    if (choice === "mine") merged[field] = localItem[field];
    // "theirs" (or unset) keeps the server value already on `merged`.
  });
  return merged;
}

// placesDiff: result of diffPlaces(). includeSelections keyed by Place.ID
// (added/removed items only). fieldSelections keyed by Place.ID -> {field: "mine"|"theirs"}
// (changed items only). Returns a plain array of Place-shaped objects (not
// `new Place(...)` instances — caller is responsible for constructing real
// Place objects from these, same as it already does when loading a guide).
export function applyPlacesMerge(placesDiff, includeSelections, fieldSelections, serverPlacesList) {
  const serverById = new Map((serverPlacesList || []).map((p) => [p.ID, p]));
  const result = new Map(serverById);

  // Removed: present locally, absent on server. Including it means putting
  // it back into the merged result.
  placesDiff.removed.forEach((localPlace) => {
    if (includeSelections[localPlace.ID]) result.set(localPlace.ID, localPlace);
  });
  // Added: present on server, absent locally. Excluding it means dropping it
  // from the merged result (it's already in `result` via serverById).
  placesDiff.added.forEach((serverPlace) => {
    if (includeSelections[serverPlace.ID] === false) result.delete(serverPlace.ID);
  });
  // Changed: apply the per-field winner on top of the server base.
  placesDiff.changed.forEach(({ local, server, changedFields }) => {
    const choices = fieldSelections[local.ID] || {};
    result.set(local.ID, applyFieldChoices(local, server, changedFields, choices));
  });

  return Array.from(result.values());
}

// eventsDiffByItinerary: result of diffEvents(). includeSelections/fieldSelections
// keyed by stableEventKey(event). Returns a flat array of Appointment-shaped
// objects across all itineraries (same flat shape guide.schedule.events already is).
export function applyEventsMerge(eventsDiffByItinerary, includeSelections, fieldSelections, serverEvents) {
  const serverByKey = new Map((serverEvents || []).map((e) => [stableEventKey(e), e]));
  const result = new Map(serverByKey);

  Object.values(eventsDiffByItinerary || {}).forEach((diff) => {
    diff.removed.forEach((localEvent) => {
      const key = stableEventKey(localEvent);
      if (includeSelections[key]) result.set(key, localEvent);
    });
    diff.added.forEach((serverEvent) => {
      const key = stableEventKey(serverEvent);
      if (includeSelections[key] === false) result.delete(key);
    });
    diff.changed.forEach(({ local, server, changedFields }) => {
      const key = stableEventKey(local);
      const choices = fieldSelections[key] || {};
      result.set(key, applyFieldChoices(local, server, changedFields, choices));
    });
  });

  return Array.from(result.values());
}

// documentChoice: "mine" | "theirs". Only meaningful when diffItinerary(...).changed is true.
export function applyItineraryMerge(documentChoice, localDocumentHtml, serverDocumentHtml) {
  return documentChoice === "mine" ? localDocumentHtml : serverDocumentHtml;
}

// guideInfoDiff: result of diffGuideInfo(). fieldSelections: { [field]: "mine"|"theirs" }.
// Returns a plain object of just the GUIDE_INFO_FIELDS, to be assigned onto
// window.guide_obj.attributes field-by-field by the caller (not a whole
// replacement — attributes also carries the bookkeeping fields this diff
// deliberately never touches).
export function applyGuideInfoMerge(guideInfoDiff, fieldSelections) {
  const merged = {};
  GUIDE_INFO_FIELDS.forEach((field) => {
    const choice = fieldSelections && fieldSelections[field];
    merged[field] = choice === "mine" ? guideInfoDiff.local?.[field] : guideInfoDiff.server?.[field];
  });
  return merged;
}

// choice: "mine" | "theirs". Only meaningful when diffGuideSettings(...).changed is true.
export function applyGuideSettingsMerge(choice, localGuideSettings, serverGuideSettings) {
  return choice === "mine" ? localGuideSettings : serverGuideSettings;
}

// Safety net: after all the specific diffs above (places, events, itinerary,
// guide info, guide settings), compare whatever's LEFT of the two guides —
// catches anything not explicitly modeled (Groups, Categories, Chapters/
// Sections, or any future Guide field) without needing bespoke per-field UI
// for each. NOT actionable per-field — just a flag that something else
// differs, so "no differences shown" above is never silently wrong about
// the guide as a whole. Takes plain JSON snapshots (e.g.
// JSON.parse(JSON.stringify(guide))), not live class instances, to stay
// decoupled from the Guide class. `attributes` is stripped entirely (not
// selectively) since it's already fully covered by diffGuideInfo against the
// authoritative guide_info doc — comparing it again here via the guide_data
// doc's own denormalized copy would risk a false positive if the two ever
// drift (they're written in the same batch, but nothing guarantees it).
//
// Compared with object keys sorted (canonicalJSON): web and mobile's Guide
// constructors create the same fields in a different order, so a plain
// JSON.stringify comparison flagged EVERY cross-platform conflict as
// "something else differs" even when nothing else did. Found 2026-10-08.
// guide_id is stripped too — it's identity, not content.
const canonicalJSON = (value) => {
  if (Array.isArray(value)) return `[${value.map(canonicalJSON).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .filter((k) => value[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonicalJSON(value[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value);
};

export function diffCatchall(localGuideSnapshot, serverGuideSnapshot) {
  const strip = (snap) => {
    const copy = { ...(snap || {}) };
    delete copy.guide_id;
    delete copy.places;
    delete copy.schedule;
    delete copy.document;
    delete copy.guide_settings;
    delete copy.attributes;
    return copy;
  };
  const local = strip(localGuideSnapshot);
  const server = strip(serverGuideSnapshot);
  return { changed: canonicalJSON(local) !== canonicalJSON(server) };
}
